import { faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import React from 'react';
import { ListGroupItem } from 'react-bootstrap';
import ConvertedUnits from 'src/components/ConvertedUnits';
import Loader from 'src/components/loader';
import ListTable from 'src/components/ui/ListTable';
import { useContainersPaginatedEditMode, useContainersPaginatedScanMode } from 'src/hooks/services/useInfinityContainer';

import ContainerRow from './ContainerRow';

const INITIAL_LIMIT = 25;

const LotContainersTable = ({
  lot,
  units,
  isExpanded,
  isScannedLot,
  initialContainerAmounts,
  scannedContainerUri,
  containerAmounts,
  containerUrisWithAmounts,
  onAmountChange,
  onToggle,
}) => {
  const isScanMode = isScannedLot && !!scannedContainerUri;

  const scanModeQuery = useContainersPaginatedScanMode(
    lot.uri,
    scannedContainerUri,
    containerUrisWithAmounts,
    isExpanded && isScanMode,
    INITIAL_LIMIT,
    `scan-${lot.uri}`
  );

  const editModeQuery = useContainersPaginatedEditMode(
    lot.uri,
    containerUrisWithAmounts,
    isExpanded && !isScanMode,
    INITIAL_LIMIT,
    `edit-${lot.uri}`
  );

  // Select the appropriate query based on mode
  const {
    data: containerData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = isScanMode ? scanModeQuery : editModeQuery;

  const isScanned = !!scannedContainerUri;
  const containers = containerData?.containers || [];
  const totalCount = containerData?.totalCount || 0;
  const hasMore = containerData?.hasMore || false;

  // Calculate remaining to fetch
  const leftToFetch = totalCount - containers.length;
  const fetchMoreCount = Math.min(INITIAL_LIMIT, leftToFetch);
  const notRenderedTitle = '[Lot Not Rendered]';

  // Define table columns
  const columns = [
    { label: 'Container', width: 4 },
    { label: 'Available', width: 4 },
    { label: 'Consumed', width: 4 },
  ];

  // Header content for collapsed and expanded views
  const headerContent = (
    <div className='list-table__header-content-wrapper'>
      <div className='list-table__title'>
        <strong className='list-table__title-text' title={lot.name || notRenderedTitle}>
          {lot.name || notRenderedTitle}
        </strong>
      </div>
      {!isExpanded && (
        <div className='list-table__subtitle-wrapper'>
          <span className='list-table__subtitle'>
            <ConvertedUnits skipTooltip displayZero quantity={lot.quantity} units={units} />&nbsp;available
          </span>
        </div>
      )}
    </div>
  );

  // Footer with load more button
  const footer =
    hasMore && hasNextPage ? (
      <button
        type='button'
        className='list-table__load-more-btn'
        disabled={isFetchingNextPage}
        onClick={() => fetchNextPage()}
      >
        {isFetchingNextPage ? (
          <Loader inline showText className='me-2' />
        ) : (
          <>Load {fetchMoreCount} more</>
        )}
      </button>
    ) : null;

  return (
    <ListTable
      icon={faBoxOpen}
      headerContent={headerContent}
      isExpanded={isExpanded}
      isHighlighted={isScanned && isScannedLot}
      columns={columns}
      footer={footer}
      onToggle={onToggle}
    >
      {isLoading ? (
        <ListGroupItem className='text-center py-4'>
          <Loader inline />
        </ListGroupItem>
      ) : containers.length === 0 ? (
        <ListGroupItem className='text-center py-3 text-muted'>
          No containers available
        </ListGroupItem>
      ) : (
        containers.map(container => (
          <ListGroupItem key={container.uri} className='px-3 py-0'>
            <ContainerRow
              container={container}
              units={units}
              amount={containerAmounts[container.uri]?.quantity || 0}
              initialAmount={initialContainerAmounts[container.uri]?.quantity || 0}
              error={containerAmounts[container.uri]?.error || ''}
              isScanned={isScannedLot && container.uri === scannedContainerUri}
              onAmountChange={onAmountChange}
            />
          </ListGroupItem>
        ))
      )}
    </ListTable>
  );
};

LotContainersTable.propTypes = {
  lot: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    name: PropTypes.string,
    quantity: PropTypes.number,
  }).isRequired,
  units: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  isScannedLot: PropTypes.bool,
  scannedContainerUri: PropTypes.string,
  containerUrisWithAmounts: PropTypes.arrayOf(PropTypes.string),
  onToggle: PropTypes.func.isRequired,
  containerAmounts: PropTypes.object.isRequired,
  onAmountChange: PropTypes.func.isRequired,
  initialContainerAmounts: PropTypes.shape({}),
};

LotContainersTable.defaultProps = {
  isScannedLot: false,
  scannedContainerUri: null,
  initialContainerAmounts: {},
  containerUrisWithAmounts: [],
};

export default LotContainersTable;

